import { Room, Wall, Point, WallSection, WallType } from '../types';
import { calculateRooms, getRoomCenter, isPointInsideRoom, getPersistentRoomId, isNearPoint, isPointOnWall, projectPointOntoLine, distanceToLine, generateStableWallId } from './floorPlanUtils'; // Import necessary helpers
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';
// 🔗 NEW: Import the enhanced shared wall system
import { 
  analyzeSharedWallSystem, 
  integrateWithExistingWallSplit, 
  updateSharedWallSystemAfterRoomChange,
  type SharedWallSystemState 
} from './sharedWallSystem';

export interface WallSplitParams {
  selectedWall: Wall;
  splitPoint: Point;
  currentWallsForFloor: Wall[];
  allRoomCustomLabels: Record<string, string>;
  currentSelectedRoom: Room | null;
  currentFloorNumber: number;
}

export interface WallSplitResult {
  newWallsForFloor: Wall[];
  newRoomCustomLabels: Record<string, string>;
  newSelectedRoomAfterSplit: Room | null;
  // 🔗 NEW: Include shared wall system state
  sharedWallSystemState?: SharedWallSystemState;
}

// 🔗 NEW: Helper function to create enhanced split segments with shared wall awareness
export const createSplitSegmentWithRoomInfo = (
  start: Point,
  end: Point,
  originalWall: Wall,
  splitPoint: Point,
  floor: number,
  parentRoomIds: string[],
  // 🔗 NEW: Optional shared wall context
  sharedWallContext?: {
    isSharedWall: boolean;
    affectedRoomIds: string[];
    preserveSharing: boolean;
  }
): Wall => {
  const originalTempId = (originalWall as any).tempId;
  const originalParentWallId = (originalWall as any).parentWallId;

  // 🔧 CRITICAL FIX: Ensure split walls inherit room ownership from original wall
  const originalOwnership = {
    originatingRoomId: (originalWall as any).originatingRoomId,
    assignedRoomIds: (originalWall as any).assignedRoomIds
  };
  
  console.log('🔧 SPLIT_SEGMENT_OWNERSHIP: Creating split segment with ownership check', {
    originalWallId: originalTempId,
    originalOwnership,
    parentRoomIds,
    segmentCoords: `(${start.x.toFixed(1)},${start.y.toFixed(1)})->(${end.x.toFixed(1)},${end.y.toFixed(1)})`
  });
  
  // If original wall lacks ownership but we have parentRoomIds, use them for ownership
  let finalOwnership = originalOwnership;
  if (!originalOwnership.originatingRoomId && !originalOwnership.assignedRoomIds && parentRoomIds.length > 0) {
    console.log('🔧 WALL_SPLIT_OWNERSHIP_REPAIR: Assigning room ownership to split segment', {
      originalWallId: originalTempId,
      parentRoomIds,
      willAssignOwnership: true
    });
    
    finalOwnership = {
      originatingRoomId: parentRoomIds[0], // Use first parent room as originating room
      assignedRoomIds: [...parentRoomIds] // Assign to all parent rooms
    };
  }
  
  // 🚨 PROACTIVE ROOM DISCOVERY: If both original wall and parentRoomIds lack ownership, find it proactively
  if (!finalOwnership.originatingRoomId && !finalOwnership.assignedRoomIds && parentRoomIds.length === 0) {
    console.log('🚨 SPLIT_SEGMENT_PROACTIVE: Both original wall and parentRoomIds lack ownership, finding room proactively');
    
    // We need walls and room labels to calculate rooms, but they're not passed to this function
    // This is a design limitation - we need to signal that ownership needs to be established upstream
    console.log('⚠️ SPLIT_SEGMENT_DESIGN_ISSUE: Cannot proactively find room - insufficient context passed to createSplitSegmentWithRoomInfo');
    
    // At minimum, ensure the segment has a flag indicating it needs ownership assignment
    finalOwnership = {
      originatingRoomId: undefined,
      assignedRoomIds: undefined
    } as any; // Type assertion to avoid lint error
  }

  const segment: Wall = {
    start: { ...start },
    end: { ...end },
    type: originalWall.type || 'None',
    floor: floor,
    // Preserve original metadata
    ...(originalTempId && { tempId: `${originalTempId}_split_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` }),
    // 🔧 FIX: Always set parentWallId for split segments
    // If original wall already has a parentWallId, use that; otherwise use the original wall's tempId
    parentWallId: originalParentWallId || originalTempId || `original_${Date.now()}`,
    // Enhanced metadata for shared wall system
    parentRoomIds: [...parentRoomIds],
    isSplitOrigin: true,
    requiresExtrusionOnMove: true,
    // 🏠 CRITICAL: Add room ownership fields to ensure split walls belong to their rooms
    ...(finalOwnership.originatingRoomId && { originatingRoomId: finalOwnership.originatingRoomId }),
    ...(finalOwnership.assignedRoomIds && { assignedRoomIds: [...finalOwnership.assignedRoomIds] }),
    // 🔗 NEW: Shared wall metadata
    ...(sharedWallContext && {
      isSharedWall: sharedWallContext.isSharedWall,
      sharedBetweenRooms: sharedWallContext.affectedRoomIds,
      preserveSharing: sharedWallContext.preserveSharing
    })
  };

  return segment;
};

export const splitWallAndProcessState = (params: WallSplitParams): WallSplitResult => {
  const {
    selectedWall,
    splitPoint,
    currentWallsForFloor,
    allRoomCustomLabels,
    currentSelectedRoom,
    currentFloorNumber,
  } = params;

  // Enhanced input validation with detailed error logging
  if (!selectedWall || !splitPoint) {
    console.error('[splitWallAndProcessState] Missing required parameters:', {
      hasSelectedWall: !!selectedWall,
      hasSplitPoint: !!splitPoint,
      selectedWall,
      splitPoint
    });
    return {
      newWallsForFloor: currentWallsForFloor,
      newRoomCustomLabels: allRoomCustomLabels,
      newSelectedRoomAfterSplit: currentSelectedRoom,
    };
  }

  // Validate wall structure
  if (!selectedWall.start || !selectedWall.end || 
      typeof selectedWall.start.x !== 'number' || typeof selectedWall.start.y !== 'number' ||
      typeof selectedWall.end.x !== 'number' || typeof selectedWall.end.y !== 'number') {
    console.error('[splitWallAndProcessState] Invalid wall structure:', selectedWall);
    return {
      newWallsForFloor: currentWallsForFloor,
      newRoomCustomLabels: allRoomCustomLabels,
      newSelectedRoomAfterSplit: currentSelectedRoom,
    };
  }

  // Validate split point
  if (typeof splitPoint.x !== 'number' || typeof splitPoint.y !== 'number' ||
      isNaN(splitPoint.x) || isNaN(splitPoint.y)) {
    console.error('[splitWallAndProcessState] Invalid split point:', splitPoint);
    return {
      newWallsForFloor: currentWallsForFloor,
      newRoomCustomLabels: allRoomCustomLabels,
      newSelectedRoomAfterSplit: currentSelectedRoom,
    };
  }

  // Validate currentWallsForFloor
  if (!Array.isArray(currentWallsForFloor)) {
    console.error('[splitWallAndProcessState] currentWallsForFloor is not an array:', currentWallsForFloor);
    return {
      newWallsForFloor: [],
      newRoomCustomLabels: allRoomCustomLabels || {},
      newSelectedRoomAfterSplit: null,
    };
  }

  const originalSelectedRoomStableId = currentSelectedRoom?.stableId;
  const originalSelectedRoomCenter = currentSelectedRoom ? getRoomCenter(currentSelectedRoom.walls) : null;

  console.log('🔧 ROOM ID DEBUG: Starting wall split with current room state', {
    originalSelectedRoomStableId,
    originalSelectedRoomCenter,
    originalRoomName: currentSelectedRoom?.name,
    roomCustomLabelsCount: Object.keys(allRoomCustomLabels || {}).length,
    roomCustomLabels: allRoomCustomLabels
  });

  let oldRooms: Room[] = [];
  let roomsAffectedBySplit: Array<{ room: Room, originalLabel?: string }> = [];
  // 🔗 NEW: Initialize shared wall system
  let initialSharedWallState: SharedWallSystemState | undefined;

  try {
    oldRooms = calculateRooms(currentWallsForFloor, allRoomCustomLabels || {}, 'labelPreservation', currentFloorNumber);
    
    // 🔗 NEW: Analyze current shared wall system
    initialSharedWallState = analyzeSharedWallSystem(currentWallsForFloor, oldRooms);
    
    console.log('🔧 ROOM ID DEBUG: Calculated old rooms before split', {
      oldRoomsCount: oldRooms.length,
      oldRooms: oldRooms.map(r => ({
        stableId: r.stableId,
        name: r.name,
        wallsCount: r.walls?.length || 0
      })),
      // 🔗 NEW: Shared wall system info
      sharedWallsCount: initialSharedWallState.sharedSegments.length,
      connectionsCount: initialSharedWallState.connections.length
    });
    
    // 🔧 SHARED WALL FIX: Identify all rooms that share the wall being split
    oldRooms.forEach((room: Room) => {
      if (!room || !room.walls || !Array.isArray(room.walls)) {
        console.warn('[splitWallAndProcessState] Invalid room structure:', room);
        return;
      }

      const wallIsPartOfRoom = room.walls.some((wall: Wall) => {
        if (!wall || !wall.start || !wall.end) return false;
        return (wall.start.x === selectedWall.start.x && wall.start.y === selectedWall.start.y &&
               wall.end.x === selectedWall.end.x && wall.end.y === selectedWall.end.y) ||
               (wall.start.x === selectedWall.end.x && wall.start.y === selectedWall.end.y &&
               wall.end.x === selectedWall.start.x && wall.end.y === selectedWall.start.y)
      });
      
      if (wallIsPartOfRoom && room.stableId) {
        const originalLabel = allRoomCustomLabels?.[room.stableId];
        roomsAffectedBySplit.push({
          room: room,
          originalLabel: originalLabel
        });
        
        console.log('🔧 SHARED WALL DEBUG: Room affected by shared wall split', {
          roomStableId: room.stableId,
          roomName: room.name,
          originalLabel: originalLabel,
          isSelectedRoom: room.stableId === originalSelectedRoomStableId,
          roomCenter: getRoomCenter(room.walls)
        });
      }
    });
  } catch (error) {
    console.error('[splitWallAndProcessState] Error calculating rooms:', error);
    // Continue with empty arrays to prevent complete failure
  }

  // 🔗 NEW: Integrate with shared wall system
  let sharedWallIntegration;
  if (initialSharedWallState) {
    sharedWallIntegration = integrateWithExistingWallSplit(
      initialSharedWallState,
      selectedWall,
      splitPoint,
      roomsAffectedBySplit
    );
    
    console.log('🔗 SHARED WALL INTEGRATION:', sharedWallIntegration.enhancedSplitInfo);
  }

  console.log('🔧 SHARED WALL DEBUG: Rooms affected by split', {
    affectedRoomsCount: roomsAffectedBySplit.length,
    isSharedWall: roomsAffectedBySplit.length > 1,
    affectedRooms: roomsAffectedBySplit.map(r => ({
      stableId: r.room.stableId,
      name: r.room.name,
      originalLabel: r.originalLabel
    })),
    // 🔗 NEW: Enhanced shared wall info
    enhancedSharedWallInfo: sharedWallIntegration?.enhancedSplitInfo
  });

  // Ensure the split point is perfectly on the wall's line to avoid precision issues
  const projectedSplitPoint = projectPointOntoLine(splitPoint, selectedWall.start, selectedWall.end);

  // 🔧 COORDINATE FIX: Ensure split point coordinates are perfectly aligned to prevent gaps
  const normalizedSplitPoint = {
    x: Math.round(projectedSplitPoint.x * 100) / 100, // Round to 2 decimal places
    y: Math.round(projectedSplitPoint.y * 100) / 100
  };

  // 🔧 SHARED WALL FIX: Create wall segments with enhanced shared wall handling
  const isSharedWall = roomsAffectedBySplit.length > 1;
  const parentRoomIds = roomsAffectedBySplit.map(r => r.room.stableId).filter(id => id) as string[];
  
  // 🔗 NEW: Prepare shared wall context for segment creation
  const sharedWallContext = sharedWallIntegration ? {
    isSharedWall: sharedWallIntegration.enhancedSplitInfo.isSharedWall,
    affectedRoomIds: parentRoomIds,
    preserveSharing: sharedWallIntegration.enhancedSplitInfo.preserveAllLabels
  } : undefined;
  
  // 🔧 NEW: Create enhanced split segments with room linking
  const segment1 = createSplitSegmentWithRoomInfo(
    selectedWall.start,
    normalizedSplitPoint,
    selectedWall,
    normalizedSplitPoint,
    currentFloorNumber,
    parentRoomIds,
    sharedWallContext
  );
  
  const segment2 = createSplitSegmentWithRoomInfo(
    normalizedSplitPoint,
    selectedWall.end,
    selectedWall,
    normalizedSplitPoint,
    currentFloorNumber,
    parentRoomIds,
    sharedWallContext
  );

  console.log('🔧 EXTRUSION SPLIT DEBUG: Created enhanced wall segments with room linking', {
    isSharedWall,
    affectedRoomsCount: roomsAffectedBySplit.length,
    parentRoomIds,
    originalWallTempId: (selectedWall as any).tempId,
    segment1: {
      coordinates: `(${segment1.start.x.toFixed(1)},${segment1.start.y.toFixed(1)})->(${segment1.end.x.toFixed(1)},${segment1.end.y.toFixed(1)})`,
      parentWallId: segment1.parentWallId,
      parentRoomIds: segment1.parentRoomIds,
      requiresExtrusionOnMove: segment1.requiresExtrusionOnMove,
      isSplitOrigin: segment1.isSplitOrigin,
      // 🔗 NEW: Shared wall info
      isSharedWall: (segment1 as any).isSharedWall,
      sharedBetweenRooms: (segment1 as any).sharedBetweenRooms
    },
    segment2: {
      coordinates: `(${segment2.start.x.toFixed(1)},${segment2.start.y.toFixed(1)})->(${segment2.end.x.toFixed(1)},${segment2.end.y.toFixed(1)})`,
      parentWallId: segment2.parentWallId,
      parentRoomIds: segment2.parentRoomIds,
      requiresExtrusionOnMove: segment2.requiresExtrusionOnMove,
      isSplitOrigin: segment2.isSplitOrigin,
      // 🔗 NEW: Shared wall info
      isSharedWall: (segment2 as any).isSharedWall,
      sharedBetweenRooms: (segment2 as any).sharedBetweenRooms
    },
    originalWall: `(${selectedWall.start.x.toFixed(1)},${selectedWall.start.y.toFixed(1)})->(${selectedWall.end.x.toFixed(1)},${selectedWall.end.y.toFixed(1)})`,
    splitPoint: `(${projectedSplitPoint.x.toFixed(1)},${projectedSplitPoint.y.toFixed(1)})`,
    metadataComparison: {
      bothHaveSameParentWallId: segment1.parentWallId === segment2.parentWallId,
      bothHaveIsSplitOrigin: segment1.isSplitOrigin === segment2.isSplitOrigin,
      bothHaveRequiresExtrusionOnMove: segment1.requiresExtrusionOnMove === segment2.requiresExtrusionOnMove,
      bothHaveSameParentRoomIds: JSON.stringify(segment1.parentRoomIds) === JSON.stringify(segment2.parentRoomIds)
    }
  });

  // Validate segments before proceeding
  const isValidSegment = (seg: Wall): boolean => {
    return seg.start && seg.end && 
           typeof seg.start.x === 'number' && typeof seg.start.y === 'number' &&
           typeof seg.end.x === 'number' && typeof seg.end.y === 'number' &&
           !isNaN(seg.start.x) && !isNaN(seg.start.y) &&
           !isNaN(seg.end.x) && !isNaN(seg.end.y);
  };

  if (!isValidSegment(segment1) || !isValidSegment(segment2)) {
    console.error('[splitWallAndProcessState] Invalid segments created:', { segment1, segment2 });
    return {
      newWallsForFloor: currentWallsForFloor,
      newRoomCustomLabels: allRoomCustomLabels,
      newSelectedRoomAfterSplit: currentSelectedRoom,
    };
  }

  // 🔧 OLD FILE COMPATIBILITY: Detect if we need to preserve ownership
  const isOldFile = currentWallsForFloor.some(w => !(w as any).originatingRoomId && !(w as any).assignedRoomIds);
  const affectedRoomIds = roomsAffectedBySplit.map(r => r.room.stableId);
  
  console.log('🔧 OLD_FILE_SPLIT_DEBUG: Analyzing file type and ownership', {
    isOldFile,
    totalWalls: currentWallsForFloor.length,
    wallsWithOwnership: currentWallsForFloor.filter(w => (w as any).originatingRoomId || (w as any).assignedRoomIds).length,
    affectedRoomsCount: roomsAffectedBySplit.length,
    affectedRoomIds,
    selectedWallId: (selectedWall as any).tempId
  });

  // Safely filter out the original wall and add new segments
  let newWallsForFloor: Wall[] = [];
  try {
    newWallsForFloor = currentWallsForFloor.filter(w => {
      if (!w || !w.start || !w.end) return false;
      
      // 🔧 COORDINATE FIX: Use normalized coordinates for comparison
      const normalizedWallStart = {
        x: Math.round(w.start.x * 100) / 100,
        y: Math.round(w.start.y * 100) / 100
      };
      const normalizedWallEnd = {
        x: Math.round(w.end.x * 100) / 100,
        y: Math.round(w.end.y * 100) / 100
      };
      const normalizedSelectedStart = {
        x: Math.round(selectedWall.start.x * 100) / 100,
        y: Math.round(selectedWall.start.y * 100) / 100
      };
      const normalizedSelectedEnd = {
        x: Math.round(selectedWall.end.x * 100) / 100,
        y: Math.round(selectedWall.end.y * 100) / 100
      };
      
      const isSelectedWall = (
        (normalizedWallStart.x === normalizedSelectedStart.x && normalizedWallStart.y === normalizedSelectedStart.y &&
         normalizedWallEnd.x === normalizedSelectedEnd.x && normalizedWallEnd.y === normalizedSelectedEnd.y) ||
        (normalizedWallStart.x === normalizedSelectedEnd.x && normalizedWallStart.y === normalizedSelectedEnd.y &&
         normalizedWallEnd.x === normalizedSelectedStart.x && normalizedWallEnd.y === normalizedSelectedStart.y)
      );
      
      return !isSelectedWall;
    });
    
    // 🔧 OLD FILE COMPATIBILITY FIX: Assign ownership to remaining walls if they lack it
    if (isOldFile && affectedRoomIds.length > 0) {
      console.log('🔧 OLD_FILE_OWNERSHIP_REPAIR: Assigning ownership to filtered walls');
      
      newWallsForFloor = newWallsForFloor.map(wall => {
        // If wall already has ownership, keep it
        if ((wall as any).originatingRoomId || (wall as any).assignedRoomIds) {
          return wall;
        }
        
        // Assign ownership from the first affected room (should be the containing room)
        const roomId = affectedRoomIds[0];
        console.log('🔧 ASSIGNING_OWNERSHIP_TO_FILTERED_WALL', {
          wallId: (wall as any).tempId,
          wallCoords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          roomId
        });
        
        return {
          ...wall,
          originatingRoomId: roomId,
          assignedRoomIds: [roomId]
        } as any;
      });
    }
    
    newWallsForFloor.push(segment1, segment2);
    
    console.log('🔧 WALL SPLIT DEBUG: Wall filtering completed', {
      originalWallsCount: currentWallsForFloor.length,
      filteredWallsCount: newWallsForFloor.length - 2, // Subtract the 2 new segments
      newSegmentsAdded: 2,
      totalWallsAfterSplit: newWallsForFloor.length
    });
  } catch (error) {
    console.error('[splitWallAndProcessState] Error filtering walls:', error);
    return {
      newWallsForFloor: currentWallsForFloor,
      newRoomCustomLabels: allRoomCustomLabels,
      newSelectedRoomAfterSplit: currentSelectedRoom,
    };
  }

  let updatedCustomLabels = { ...(allRoomCustomLabels || {}) };
  const newLabelsToSet: Record<string, string> = {};
  const oldLabelsToDelete: string[] = [];
  let provisionalNewRoomsForLabeling: Room[] = [];

  try {
    provisionalNewRoomsForLabeling = calculateRooms(newWallsForFloor, allRoomCustomLabels || {}, 'labelPreservation', currentFloorNumber);
    
    console.log('🔧 WALL SPLIT DEBUG: Provisional room calculation completed', {
      inputWallsCount: newWallsForFloor.length,
      provisionalRoomsCount: provisionalNewRoomsForLabeling.length,
      originalRoomsCount: oldRooms.length,
      roomsAffectedBySplit: roomsAffectedBySplit.length,
      provisionalRoomNames: provisionalNewRoomsForLabeling.map(r => r.name || 'Unnamed'),
      provisionalRoomAreas: provisionalNewRoomsForLabeling.map(r => r.area.toFixed(2))
    });
    
    // 🔧 VALIDATION: Ensure we haven't lost rooms during split
    if (provisionalNewRoomsForLabeling.length < oldRooms.length - 1) {
      console.warn('🔧 WALL SPLIT WARNING: Fewer rooms detected after split than expected', {
        originalRoomsCount: oldRooms.length,
        provisionalRoomsCount: provisionalNewRoomsForLabeling.length,
        expectedMinimumRooms: oldRooms.length, // Should maintain or gain rooms
        wallsInput: newWallsForFloor.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
      });
    }
  } catch (error) {
    console.error('[splitWallAndProcessState] Error calculating provisional rooms:', error);
    // Continue with empty array to prevent complete failure
  }

  // Safely process room label transfers
  roomsAffectedBySplit.forEach(affected => {
    try {
      if (affected.originalLabel && affected.room.stableId) {
        const originalOtherWalls = affected.room.walls.filter(w => {
          if (!w || !w.start || !w.end) return false;
          return !((w.start.x === selectedWall.start.x && w.start.y === selectedWall.start.y && w.end.x === selectedWall.end.x && w.end.y === selectedWall.end.y) ||
                   (w.start.x === selectedWall.end.x && w.start.y === selectedWall.end.y && w.end.x === selectedWall.start.x && w.end.y === selectedWall.start.y))
        });

        // 🔧 SHARED WALL FIX: Enhanced label transfer for shared walls
        console.log('🔧 SHARED WALL DEBUG: Processing label transfer for room', {
          roomName: affected.room.name,
          originalLabel: affected.originalLabel,
          originalWallsCount: affected.room.walls.length,
          otherWallsCount: originalOtherWalls.length,
          isSharedWall,
          roomCenter: getRoomCenter(affected.room.walls)
        });

        let bestMatchRoom: Room | null = null;
        let bestMatchScore = 0;

        // Process each provisional room to find the best match
        for (const newRoom of provisionalNewRoomsForLabeling) {
          if (!newRoom || !newRoom.walls) {
            console.warn('[splitWallAndProcessState] Invalid new room structure:', newRoom);
            continue;
          }

          const newRoomContainsSegment1 = newRoom.walls.some((nw: Wall) => {
            if (!nw || !nw.start || !nw.end) return false;
            return (nw.start.x === segment1.start.x && nw.start.y === segment1.start.y && nw.end.x === segment1.end.x && nw.end.y === segment1.end.y) || 
                   (nw.start.x === segment1.end.x && nw.start.y === segment1.end.y && nw.end.x === segment1.start.x && nw.end.y === segment1.start.y);
          });

          const newRoomContainsSegment2 = newRoom.walls.some((nw: Wall) => {
            if (!nw || !nw.start || !nw.end) return false;
            return (nw.start.x === segment2.start.x && nw.start.y === segment2.start.y && nw.end.x === segment2.end.x && nw.end.y === segment2.end.y) || 
                   (nw.start.x === segment2.end.x && nw.start.y === segment2.end.y && nw.end.x === segment2.start.x && nw.end.y === segment2.start.y);
          });

          // 🔧 SHARED WALL FIX: A room must contain at least one split segment to be considered
          if (newRoomContainsSegment1 || newRoomContainsSegment2) {
            let sharedOtherWallCount = 0;
            let totalOtherWallsInNewRoom = 0;
            
            newRoom.walls.forEach((nw: Wall) => {
              if (!nw || !nw.start || !nw.end) return;
              
              const nwIsSegment1 = (nw.start.x === segment1.start.x && nw.start.y === segment1.start.y && nw.end.x === segment1.end.x && nw.end.y === segment1.end.y) || 
                                   (nw.start.x === segment1.end.x && nw.start.y === segment1.end.y && nw.end.x === segment1.start.x && nw.end.y === segment1.start.y);
              const nwIsSegment2 = (nw.start.x === segment2.start.x && nw.start.y === segment2.start.y && nw.end.x === segment2.end.x && nw.end.y === segment2.end.y) || 
                                   (nw.start.x === segment2.end.x && nw.start.y === segment2.end.y && nw.end.x === segment2.start.x && nw.end.y === segment2.start.y);

              if (!nwIsSegment1 && !nwIsSegment2) {
                totalOtherWallsInNewRoom++;
                if (originalOtherWalls.some(ow => {
                  if (!ow || !ow.start || !ow.end) return false;
                  return (ow.start.x === nw.start.x && ow.start.y === nw.start.y && ow.end.x === nw.end.x && ow.end.y === nw.end.y) ||
                         (ow.start.x === nw.end.x && ow.start.y === nw.end.y && ow.end.x === nw.start.x && ow.end.y === nw.start.y)
                })) {
                  sharedOtherWallCount++;
                }
              }
            });

            // 🔧 SHARED WALL FIX: Calculate match score based on wall overlap and geometric similarity
            const wallOverlapRatio = originalOtherWalls.length > 0 ? sharedOtherWallCount / originalOtherWalls.length : 0;
            const newRoomCenter = getRoomCenter(newRoom.walls);
            const originalRoomCenter = getRoomCenter(affected.room.walls);
            const centerDistance = Math.hypot(newRoomCenter.x - originalRoomCenter.x, newRoomCenter.y - originalRoomCenter.y);
            const maxExpectedDistance = 100; // Allow some movement due to split
            const centerScore = Math.max(0, 1 - (centerDistance / maxExpectedDistance));
            
            // Combined score: wall overlap (70%) + center proximity (30%)
            const matchScore = (wallOverlapRatio * 0.7) + (centerScore * 0.3);
            
            console.log('🔧 SHARED WALL DEBUG: Evaluating room match', {
              newRoomName: newRoom.name,
              originalRoomName: affected.room.name,
              containsSegment1: newRoomContainsSegment1,
              containsSegment2: newRoomContainsSegment2,
              sharedOtherWallCount,
              totalOriginalOtherWalls: originalOtherWalls.length,
              wallOverlapRatio: wallOverlapRatio.toFixed(2),
              centerDistance: centerDistance.toFixed(1),
              centerScore: centerScore.toFixed(2),
              matchScore: matchScore.toFixed(2)
            });

            if (matchScore > bestMatchScore && matchScore > 0.5) { // Require at least 50% match
              bestMatchScore = matchScore;
              bestMatchRoom = newRoom;
            }
          }
        }

        // 🔧 TypeScript: Explicit type checking to ensure bestMatchRoom is properly typed
        const typedBestMatchRoom: Room | null = bestMatchRoom;
        if (typedBestMatchRoom && typedBestMatchRoom.stableId && affected.originalLabel) {
          newLabelsToSet[typedBestMatchRoom.stableId] = affected.originalLabel;
          if (affected.room.stableId) {
            oldLabelsToDelete.push(affected.room.stableId);
          }
          
          console.log('🔧 SHARED WALL DEBUG: Label transfer completed', {
            fromRoomStableId: affected.room.stableId,
            toRoomStableId: typedBestMatchRoom.stableId,
            transferredLabel: affected.originalLabel,
            matchScore: bestMatchScore.toFixed(2),
            fromRoomName: affected.room.name,
            toRoomName: typedBestMatchRoom.name
          });
        } else {
          console.warn('🔧 SHARED WALL DEBUG: No suitable room found for label transfer', {
            originalRoomStableId: affected.room.stableId,
            originalLabel: affected.originalLabel,
            bestMatchScore: bestMatchScore.toFixed(2),
            bestMatchRoom: typedBestMatchRoom?.name || 'none'
          });
        }
      }
    } catch (error) {
      console.error('[splitWallAndProcessState] Error processing room label transfer:', error);
    }
  });

  // Apply label updates
  Object.entries(newLabelsToSet).forEach(([stableId, label]) => {
    updatedCustomLabels[stableId] = label;
  });
  
  oldLabelsToDelete.forEach(oldStableId => {
    if (updatedCustomLabels[oldStableId] && !Object.values(newLabelsToSet).includes(updatedCustomLabels[oldStableId])) {
      delete updatedCustomLabels[oldStableId];
    }
  });

  let finalNewRoomsAfterSplit: Room[] = [];
  let newSelectedRoomAfterSplit: Room | null = null;

  try {
    finalNewRoomsAfterSplit = calculateRooms(newWallsForFloor, updatedCustomLabels, 'labelPreservation', currentFloorNumber);

    console.log('🔧 ROOM ID DEBUG: Calculated final rooms after split', {
      finalRoomsCount: finalNewRoomsAfterSplit.length,
      oldRoomsCount: oldRooms.length,
      finalRooms: finalNewRoomsAfterSplit.map(r => ({
        stableId: r.stableId,
        name: r.name,
        wallsCount: r.walls?.length || 0
      })),
      oldRooms: oldRooms.map(r => ({
        stableId: r.stableId,
        name: r.name,
        wallsCount: r.walls?.length || 0
      })),
      updatedCustomLabels: updatedCustomLabels,
      wallsBeforeSplit: currentWallsForFloor.length,
      wallsAfterSplit: newWallsForFloor.length
    });

    if (originalSelectedRoomStableId) {
      const originalSelectedRoomLabel = roomsAffectedBySplit.find(ars => ars.room.stableId === originalSelectedRoomStableId)?.originalLabel;
      
      console.log('🔧 ROOM ID DEBUG: Looking for selected room after split', {
        originalSelectedRoomStableId,
        originalSelectedRoomLabel,
        originalSelectedRoomCenter
      });
      
      // Attempt 1: Find by the new stable ID that might have inherited the label
      const newStableIdWithOldLabel = Object.keys(updatedCustomLabels).find(newId =>
        finalNewRoomsAfterSplit.some((r: Room) => r.stableId === newId) &&
        updatedCustomLabels[newId] === originalSelectedRoomLabel
      );
      
      console.log('🔧 ROOM ID DEBUG: Attempt 1 - Find by label inheritance', {
        newStableIdWithOldLabel,
        searchingForLabel: originalSelectedRoomLabel
      });
      
      if (newStableIdWithOldLabel) {
        newSelectedRoomAfterSplit = finalNewRoomsAfterSplit.find((r: Room) => r.stableId === newStableIdWithOldLabel) || null;
        console.log('🔧 ROOM ID DEBUG: Found room by label inheritance', {
          foundRoomStableId: newSelectedRoomAfterSplit?.stableId,
          foundRoomName: newSelectedRoomAfterSplit?.name
        });
      }
      
      // Attempt 2: If not found by label transfer, try finding by original stable ID
      if (!newSelectedRoomAfterSplit) {
        newSelectedRoomAfterSplit = finalNewRoomsAfterSplit.find((r: Room) => r.stableId === originalSelectedRoomStableId) || null;
        console.log('🔧 ROOM ID DEBUG: Attempt 2 - Find by original stable ID', {
          foundRoomStableId: newSelectedRoomAfterSplit?.stableId,
          foundRoomName: newSelectedRoomAfterSplit?.name
        });
      }
      
      // Attempt 3: If still not found, try to find the new room that contains the old center
      if (!newSelectedRoomAfterSplit && originalSelectedRoomCenter) {
        newSelectedRoomAfterSplit = finalNewRoomsAfterSplit.find((r: Room) => {
          try {
            const containsCenter = isPointInsideRoom(originalSelectedRoomCenter, r.walls);
            console.log('🔧 ROOM ID DEBUG: Checking if room contains original center', {
              roomStableId: r.stableId,
              roomName: r.name,
              containsCenter
            });
            return containsCenter;
          } catch (error) {
            console.error('[splitWallAndProcessState] Error checking point inside room:', error);
            return false;
          }
        }) || null;
        console.log('🔧 ROOM ID DEBUG: Attempt 3 - Find by center containment', {
          foundRoomStableId: newSelectedRoomAfterSplit?.stableId,
          foundRoomName: newSelectedRoomAfterSplit?.name
        });
      }
    }
  } catch (error) {
    console.error('[splitWallAndProcessState] Error calculating final rooms after split:', error);
    return {
      newWallsForFloor: currentWallsForFloor,
      newRoomCustomLabels: allRoomCustomLabels,
      newSelectedRoomAfterSplit: currentSelectedRoom,
    };
  }

  // 🔗 NEW: Update shared wall system with new room configuration
  let finalSharedWallState: SharedWallSystemState | undefined;
  if (initialSharedWallState && sharedWallIntegration) {
    // Create a simple migration map for room ID preservation
    const roomMigrationMap: Record<string, string> = {};
    oldRooms.forEach(oldRoom => {
      if (oldRoom.stableId) {
        const matchingNewRoom = finalNewRoomsAfterSplit.find(newRoom => 
          newRoom.stableId === oldRoom.stableId || 
          (newRoom.stableId && oldRoom.stableId && 
           updatedCustomLabels[newRoom.stableId] === updatedCustomLabels[oldRoom.stableId])
        );
        if (matchingNewRoom?.stableId && oldRoom.stableId) {
          roomMigrationMap[oldRoom.stableId] = matchingNewRoom.stableId;
        }
      }
    });

    finalSharedWallState = updateSharedWallSystemAfterRoomChange(
      sharedWallIntegration.updatedSystemState,
      newWallsForFloor,
      finalNewRoomsAfterSplit,
      roomMigrationMap
    );

    console.log('🔗 SHARED WALL SYSTEM UPDATE COMPLETE:', {
      roomMigrationMapSize: Object.keys(roomMigrationMap).length,
      finalSharedWallsCount: finalSharedWallState.sharedSegments.length,
      finalConnectionsCount: finalSharedWallState.connections.length
    });
  }

  console.log('🔧 WALL SPLIT DEBUG: Operation completed successfully', {
    finalNewRoomsCount: finalNewRoomsAfterSplit.length,
    newWallsCount: newWallsForFloor.length,
    newLabelsCount: Object.keys(updatedCustomLabels).length,
    selectedRoomFound: !!newSelectedRoomAfterSplit,
    selectedRoomStableId: newSelectedRoomAfterSplit?.stableId,
    selectedRoomName: newSelectedRoomAfterSplit?.name,
    // 🔗 NEW: Shared wall system results
    sharedWallSystemIncluded: !!finalSharedWallState
  });

  return {
    newWallsForFloor,
    newRoomCustomLabels: updatedCustomLabels,
    newSelectedRoomAfterSplit,
    // 🔗 NEW: Include enhanced shared wall system state
    sharedWallSystemState: finalSharedWallState
  };
};

// Helper function to check if two points are a_very_small_distance_apart (for deduplication)
export const pointsAreEffectivelyEqual = (p1: Point, p2: Point, tolerance = 0.01): boolean => {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y) < tolerance;
};

// Helper to sort points along a line segment (from start to end of the segment)
const sortPointsAlongWall = (points: Point[], wallStart: Point, wallEnd: Point): Point[] => {
  const dxWall = wallEnd.x - wallStart.x;
  // const dyWall = wallEnd.y - wallStart.y; // Not directly used in this simplified sort

  return points.sort((a, b) => {
    if (Math.abs(dxWall) > 0.01) { // Prioritize X for non-vertical walls
      return (a.x - wallStart.x) - (b.x - wallStart.x);
    } else { // Sort by Y for vertical walls
      return (a.y - wallStart.y) - (b.y - wallStart.y);
    }
  });
};

// Helper to get midpoint of a segment
const getSegmentMidpoint = (p1: Point, p2: Point): Point => {
  return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
};

// Assuming orientation is available from floorPlanUtils or defined here for local use
const orientation = (p: Point, q: Point, r: Point): number => {
  const tolerance = 1e-7; 
  const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
  if (Math.abs(val) < tolerance) return 0; 
  return (val > 0) ? 1 : 2; 
};

const INTERACTION_POINT_CLEARANCE_TOLERANCE = 0.05; // 5cm world units

export const getExposedSegments = (
  targetWall: Wall,
  allWalls: Wall[],
  utils: {
    isPointOnWall: (point: Point, wall: Wall, threshold?: number) => Point | null;
    getLineIntersectionPoint: (l1s: Point, l1e: Point, l2s: Point, l2e: Point) => Point | null;
    isNearPoint: (p1: Point, p2: Point, threshold?: number) => boolean;
  }
): Wall[] => {
  const { isPointOnWall, getLineIntersectionPoint, isNearPoint } = utils;

  const junctionPointsOnTargetWall: Point[] = [{...targetWall.start}, {...targetWall.end}];
  const verySmallDistance = 0.01;

  for (const otherWall of allWalls) {
    if (
      (pointsAreEffectivelyEqual(otherWall.start, targetWall.start) && pointsAreEffectivelyEqual(otherWall.end, targetWall.end)) ||
      (pointsAreEffectivelyEqual(otherWall.start, targetWall.end) && pointsAreEffectivelyEqual(otherWall.end, targetWall.start))
    ) {
      continue; 
    }

    if (isPointOnWall(otherWall.start, targetWall, verySmallDistance)) {
      junctionPointsOnTargetWall.push({...otherWall.start});
    }
    if (isPointOnWall(otherWall.end, targetWall, verySmallDistance)) {
      junctionPointsOnTargetWall.push({...otherWall.end});
    }

    const intersection = getLineIntersectionPoint(targetWall.start, targetWall.end, otherWall.start, otherWall.end);
    if (intersection) {
      const onTargetStrict = isPointOnWall(intersection, targetWall, verySmallDistance) && 
                           !pointsAreEffectivelyEqual(intersection, targetWall.start) && 
                           !pointsAreEffectivelyEqual(intersection, targetWall.end);
      const onOtherStrict = isPointOnWall(intersection, otherWall, verySmallDistance) &&
                          !pointsAreEffectivelyEqual(intersection, otherWall.start) &&
                          !pointsAreEffectivelyEqual(intersection, otherWall.end);
      
      if (onTargetStrict && onOtherStrict) { // True T-intersections or X-intersections within segments
        junctionPointsOnTargetWall.push({...intersection});
      }
    }
  }

  const uniqueSortedPoints = sortPointsAlongWall(
    junctionPointsOnTargetWall.filter((p, index, self) =>
      index === self.findIndex(t => pointsAreEffectivelyEqual(t, p))
    ),
    targetWall.start,
    targetWall.end
  );
  console.log('[getExposedSegments] Unique sorted junction points on target wall:', JSON.parse(JSON.stringify(uniqueSortedPoints)));


  if (uniqueSortedPoints.length < 2) {
    console.log('[getExposedSegments] Less than 2 unique points, checking original wall eligibility.');
    const midpointOriginal = getSegmentMidpoint(targetWall.start, targetWall.end);
    const isOriginalEligible = isInteractionPointEligibleForTool(midpointOriginal, allWalls, targetWall);
    console.log('[getExposedSegments] Midpoint of original wall:', JSON.parse(JSON.stringify(midpointOriginal)), 'Is eligible:', isOriginalEligible);
    if (isOriginalEligible) {
        return [{ ...targetWall, type: targetWall.type, floor: targetWall.floor }];
    }
    return [];
  }

  const segments: Wall[] = [];
  for (let i = 0; i < uniqueSortedPoints.length - 1; i++) {
    const p1 = uniqueSortedPoints[i];
    const p2 = uniqueSortedPoints[i + 1];
    if (!pointsAreEffectivelyEqual(p1, p2)) {
      segments.push({
        start: p1,
        end: p2,
        type: targetWall.type, 
        floor: targetWall.floor,
      });
    }
  }
  console.log('[getExposedSegments] Potential segments created:', JSON.parse(JSON.stringify(segments)));

  const finalExposedSegments = segments.filter(segment => {
    const midpoint = getSegmentMidpoint(segment.start, segment.end);
    const isEligible = isInteractionPointEligibleForTool(midpoint, allWalls, segment);
    console.log('[getExposedSegments] Checking segment:', JSON.parse(JSON.stringify(segment)), 'Midpoint:', JSON.parse(JSON.stringify(midpoint)), 'Is eligible:', isEligible);
    return isEligible;
  });
  console.log('[getExposedSegments] Final exposed segments returned:', JSON.parse(JSON.stringify(finalExposedSegments)));
  return finalExposedSegments;
};


export const isInteractionPointEligibleForTool = (
  interactionPoint: Point,
  allWalls: Wall[], 
  selectedWall: Wall // The wall segment whose midpoint (interactionPoint) is being tested
): boolean => {
  if (!interactionPoint || !selectedWall) {
    // console.warn("isInteractionPointEligibleForTool: Missing interactionPoint or selectedWall");
    return false;
  }

  for (const otherWall of allWalls) {
    // Skip if otherWall is identical to selectedWall (the segment being tested)
    const isIdenticalToSelectedSegment = 
        (pointsAreEffectivelyEqual(otherWall.start, selectedWall.start) && pointsAreEffectivelyEqual(otherWall.end, selectedWall.end)) ||
        (pointsAreEffectivelyEqual(otherWall.start, selectedWall.end) && pointsAreEffectivelyEqual(otherWall.end, selectedWall.start));
    if (isIdenticalToSelectedSegment) {
      continue;
    }

    // Check 1: Is the interactionPoint (midpoint of selectedWall) AT an endpoint of another distinct wall?
    // This signifies a T-junction or corner where the midpoint itself is the junction.
    if (isNearPoint(interactionPoint, otherWall.start, INTERACTION_POINT_CLEARANCE_TOLERANCE) ||
        isNearPoint(interactionPoint, otherWall.end, INTERACTION_POINT_CLEARANCE_TOLERANCE)) {
      console.log(`[isInteractionPointEligibleForTool] Midpoint ${JSON.stringify(interactionPoint)} of segment ${JSON.stringify(selectedWall)} is near endpoint of otherWall ${JSON.stringify(otherWall)}. Ineligible due to Check 1.`);
      return false; 
    }

    // Check 2: Does the interactionPoint (midpoint of selectedWall) lie ON the segment of another distinct wall?
    // This implies selectedWall's midpoint is part of a T-junction or crossing with otherWall.
    // We must ensure otherWall is not simply collinear and overlapping with selectedWall (e.g. its parent).
    const pointOnOtherWallSegment = isPointOnWall(interactionPoint, otherWall, INTERACTION_POINT_CLEARANCE_TOLERANCE);
    if (pointOnOtherWallSegment !== null) {
      // If the midpoint is on another wall segment, we need to ensure this isn't just the segment itself
      // or its direct parent if they are perfectly collinear and overlapping.
      // The primary concern is if 'otherWall' *crosses* or forms a T-junction at 'interactionPoint'.
      
      // Check for collinearity:
      const selectedWallOrientationWithOtherStart = orientation(selectedWall.start, selectedWall.end, otherWall.start);
      const selectedWallOrientationWithOtherEnd = orientation(selectedWall.start, selectedWall.end, otherWall.end);

      if (selectedWallOrientationWithOtherStart === 0 && selectedWallOrientationWithOtherEnd === 0) {
        // They are collinear. If interactionPoint is on otherWall, it's fine as long as otherWall isn't "capping" it.
        // This case should ideally be handled by the endpoint check (Check 1) if the midpoint aligns with an endpoint.
        // If they are collinear and the midpoint is on otherWall, it means selectedWall is part of otherWall or vice-versa.
        // This doesn't automatically make it ineligible unless it's at a junction point.
        // The original `isPointOnWall` check is problematic here if `otherWall` is the parent.
        // Let's refine: a midpoint is ineligible if it's on an `otherWall` AND that `otherWall` is NOT collinear with `selectedWall`.
        // Or, if they ARE collinear, but `otherWall` forms a junction (e.g. `selectedWall` is a small part of a larger structure).
        // This is where the definition of "exposed" is critical.
        // An exposed segment's midpoint should not be covered by a *crossing* or *T-junctioning* different wall.
        // If otherWall is collinear and contains selectedWall, the midpoint is on it. This is not necessarily a disqualifier.
        // The disqualifier is if this point is a junction point with a *third* wall, or an endpoint of `otherWall`.
        // The previous endpoint check (Check 1) should cover most junction cases.
        // This Check 2 is for when the midpoint lies *along the body* of another wall (T-junction).
        
        // If otherWall is collinear with selectedWall, and the midpoint is on it,
        // this doesn't make it ineligible by itself. It's only ineligible if this point
        // also happens to be a junction point (which Check 1 would catch).
        // So, if they are collinear, we can 'continue' this specific check for this otherWall.
        continue; 
      }
      // If not collinear, and midpoint is on otherWall, it's a T-junction or crossing.
      console.log(`[isInteractionPointEligibleForTool] Midpoint ${JSON.stringify(interactionPoint)} of segment ${JSON.stringify(selectedWall)} lies on non-collinear otherWall ${JSON.stringify(otherWall)}. Ineligible due to Check 2.`);
      return false;
    }
  }
  console.log(`[isInteractionPointEligibleForTool] Midpoint ${JSON.stringify(interactionPoint)} of segment ${JSON.stringify(selectedWall)} is ELIGIBLE.`);
  return true; // Interaction point is clear of other walls
};

// 🆕 ENHANCED WALL SEGMENTATION: Simplified approach using aligned room endpoints
// This implements the user's suggestion to use natural room boundaries for segmentation

/**
 * Finds room endpoints that align with a wall on the same axis
 * This creates natural segmentation points where walls transition from exterior to interior
 */
export function findAlignedRoomEndpoints(wall: Wall, rooms: Room[]): Point[] {
  const alignedPoints: Point[] = [];
  const tolerance = 5; // pixels
  
  // Get all room endpoints (corners)
  const allEndpoints = rooms.flatMap(room => 
    room.walls.flatMap(roomWall => [roomWall.start, roomWall.end])
  );
  
  // Check if any endpoints align with this wall on same axis
  for (const endpoint of allEndpoints) {
    // Check if endpoint is on the wall line
    const distanceToWall = distanceToLine(endpoint, wall.start, wall.end);
    if (distanceToWall < tolerance) {
      // Check if it's between wall endpoints (not at the ends)
      const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      if (wallLength > 0) {
        const t = ((endpoint.x - wall.start.x) * (wall.end.x - wall.start.x) + 
                   (endpoint.y - wall.start.y) * (wall.end.y - wall.start.y)) / (wallLength * wallLength);
        
        // Only include points that are truly on the wall segment (not at ends)
        if (t > 0.1 && t < 0.9) {
          // Check if this point is not already added
          const isDuplicate = alignedPoints.some(existing => 
            Math.hypot(existing.x - endpoint.x, existing.y - endpoint.y) < 2
          );
          
          if (!isDuplicate) {
            alignedPoints.push({...endpoint});
          }
        }
      }
    }
  }
  
  return alignedPoints;
}

/**
 * Creates wall sections based on aligned room endpoints
 * Each section represents a segment that can have its own wall type
 */
export function createWallSections(wall: Wall, rooms: Room[], allWalls: Wall[]): WallSection[] {
  const alignedPoints = findAlignedRoomEndpoints(wall, rooms);
  
  console.log('🔧 createWallSections: Aligned points analysis', {
    wallId: generateStableWallId(wall.start, wall.end),
    wallCoords: `${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)} -> ${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}`,
    wallLength: Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y).toFixed(1),
    alignedPointsFound: alignedPoints.length,
    alignedPoints: alignedPoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`),
    roomsCount: rooms.length,
    roomNames: rooms.map(r => r.name || 'unnamed')
  });
  
  if (alignedPoints.length === 0) {
    // No segmentation needed - entire wall is one section
    const isExterior = isWallExteriorFacing(wall, allWalls);
    console.log('🔧 createWallSections: No aligned points, creating single section', {
      isExterior,
      wallType: wall.type
    });
    return [{
      startPercent: 0,
      endPercent: 1,
      type: wall.type,
      isExterior,
      exposureLevel: isExterior ? 'fully_exposed' : 'interior'
    }];
  }
  
  // Sort aligned points along the wall direction
  const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
  const wallLength = Math.hypot(wallVector.x, wallVector.y);
  
  if (wallLength === 0) {
    // Degenerate wall
    return [{
      startPercent: 0,
      endPercent: 1,
      type: wall.type,
      isExterior: false,
      exposureLevel: 'interior'
    }];
  }
  
  const sortedPoints = alignedPoints
    .map(point => {
      const toPoint = { x: point.x - wall.start.x, y: point.y - wall.start.y };
      const projection = (toPoint.x * wallVector.x + toPoint.y * wallVector.y) / (wallLength * wallLength);
      return {
        point,
        distance: projection * wallLength,
        percent: projection
      };
    })
    .filter(item => item.percent > 0.01 && item.percent < 0.99) // Exclude very close to endpoints
    .sort((a, b) => a.distance - b.distance);

  // Create sections between segmentation points
  const sections: WallSection[] = [];
  const allSegmentPoints = [
    { point: wall.start, distance: 0, percent: 0 },
    ...sortedPoints,
    { point: wall.end, distance: wallLength, percent: 1 }
  ];

  for (let i = 0; i < allSegmentPoints.length - 1; i++) {
    const startPoint = allSegmentPoints[i];
    const endPoint = allSegmentPoints[i + 1];
    
    // Only create sections that are meaningful in size
    if (endPoint.percent - startPoint.percent > 0.05) { // Minimum 5% of wall length
      const segmentStart = {
        x: wall.start.x + startPoint.percent * wallVector.x,
        y: wall.start.y + startPoint.percent * wallVector.y
      };
      const segmentEnd = {
        x: wall.start.x + endPoint.percent * wallVector.x,
        y: wall.start.y + endPoint.percent * wallVector.y
      };
      
      // Determine if this segment is exterior-facing
      const isExterior = isSegmentExteriorFacing(segmentStart, segmentEnd, allWalls, rooms);
      
      sections.push({
        startPercent: startPoint.percent,
        endPercent: endPoint.percent,
        type: wall.type, // Initially inherit wall type
        isExterior,
        exposureLevel: isExterior ? 'fully_exposed' : 'interior'
      });
    }
  }

  return sections.length > 0 ? sections : [{
    startPercent: 0,
    endPercent: 1,
    type: wall.type,
    isExterior: isWallExteriorFacing(wall, allWalls),
    exposureLevel: 'interior'
  }];
}

/**
 * Determines if a wall segment is exterior-facing
 * Uses simple heuristics: segments near building perimeter or with specific types
 */
function isSegmentExteriorFacing(start: Point, end: Point, allWalls: Wall[], rooms: Room[]): boolean {
  // Create a temporary wall for analysis
  const tempWall: Wall = { start, end, type: 'None', floor: 0 };
  return isWallExteriorFacing(tempWall, allWalls);
}

/**
 * Simple exterior wall detection using building perimeter analysis
 */
function isWallExteriorFacing(wall: Wall, allWalls: Wall[]): boolean {
  // If wall has a specific type (HLP, PW), it's likely exterior
  if (wall.type && wall.type !== 'None') {
    return true;
  }
  
  // Get building bounds
  const allPoints = allWalls.flatMap(w => [w.start, w.end]);
  if (allPoints.length === 0) return true;
  
  const minX = Math.min(...allPoints.map(p => p.x));
  const maxX = Math.max(...allPoints.map(p => p.x));
  const minY = Math.min(...allPoints.map(p => p.y));
  const maxY = Math.max(...allPoints.map(p => p.y));
  
  const tolerance = 10; // pixels
  
  // Check if wall is on building perimeter
  const wallMinX = Math.min(wall.start.x, wall.end.x);
  const wallMaxX = Math.max(wall.start.x, wall.end.x);
  const wallMinY = Math.min(wall.start.y, wall.end.y);
  const wallMaxY = Math.max(wall.start.y, wall.end.y);
  
  const onLeftEdge = Math.abs(wallMinX - minX) <= tolerance && Math.abs(wallMaxX - minX) <= tolerance;
  const onRightEdge = Math.abs(wallMinX - maxX) <= tolerance && Math.abs(wallMaxX - maxX) <= tolerance;
  const onTopEdge = Math.abs(wallMinY - minY) <= tolerance && Math.abs(wallMaxY - minY) <= tolerance;
  const onBottomEdge = Math.abs(wallMinY - maxY) <= tolerance && Math.abs(wallMaxY - maxY) <= tolerance;
  
  return onLeftEdge || onRightEdge || onTopEdge || onBottomEdge;
}

/**
 * Finds which wall section contains a given point
 */
export function findSectionAtPoint(point: Point, wall: Wall, sections: WallSection[]): WallSection | null {
  if (!sections || sections.length === 0) {
    console.log('🔧 findSectionAtPoint: No sections provided');
    return null;
  }
  
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  if (wallLength === 0) {
    console.log('🔧 findSectionAtPoint: Zero-length wall, returning first section');
    return sections[0];
  }
  
  // Project point onto wall to get position percentage
  const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
  const toPoint = { x: point.x - wall.start.x, y: point.y - wall.start.y };
  const projection = (toPoint.x * wallVector.x + toPoint.y * wallVector.y) / (wallLength * wallLength);
  
  console.log('🔧 findSectionAtPoint: Projection calculation', {
    wallStart: wall.start,
    wallEnd: wall.end,
    wallLength: wallLength.toFixed(2),
    clickPoint: point,
    wallVector,
    toPoint,
    projection: projection.toFixed(4),
    sectionsCount: sections.length,
    sectionRanges: sections.map((section, index) => ({
      index,
      startPercent: section.startPercent.toFixed(4),
      endPercent: section.endPercent.toFixed(4),
      contains: projection >= section.startPercent && projection <= section.endPercent
    }))
  });
  
  // Find section that contains this position
  for (const section of sections) {
    if (projection >= section.startPercent && projection <= section.endPercent) {
      console.log('🔧 findSectionAtPoint: Section found', {
        sectionIndex: sections.indexOf(section),
        startPercent: section.startPercent.toFixed(4),
        endPercent: section.endPercent.toFixed(4),
        projection: projection.toFixed(4)
      });
      return section;
    }
  }
  
  console.log('🔧 findSectionAtPoint: No section contains projection', {
    projection: projection.toFixed(4),
    availableSections: sections.map(s => `${s.startPercent.toFixed(4)}-${s.endPercent.toFixed(4)}`)
  });
  return null;
}

/**
 * Updates a specific section's wall type
 */
export function updateSectionType(sections: WallSection[], sectionIndex: number, newType: WallType): WallSection[] {
  return sections.map((section, index) => 
    index === sectionIndex 
      ? { ...section, type: newType }
      : section
  );
}

/**
 * Calculates wall length by section types for accurate measurements
 */
export function calculateWallLengthByType(wall: Wall, sections: WallSection[]): Record<WallType, number> {
  const result: Record<WallType, number> = { 'HLP': 0, 'PW': 0, 'SW': 0, 'None': 0 };
  
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  
  for (const section of sections) {
    const sectionLength = wallLength * (section.endPercent - section.startPercent);
    const lengthInMeters = sectionLength / PIXELS_PER_METER;
    const sectionType = section.type as WallType;
    if (sectionType in result) {
      result[sectionType] += lengthInMeters;
    }
  }
  
  return result;
}

/**
 * Gets the effective wall type for rendering (primary type from sections)
 */
export function getEffectiveWallType(wall: Wall): WallType {
  if (!wall.sections || wall.sections.length === 0) {
    return wall.type;
  }
  
  // Return the type of the largest section
  const largestSection = wall.sections.reduce((largest: WallSection, current: WallSection) => 
    (current.endPercent - current.startPercent) > (largest.endPercent - largest.startPercent) 
      ? current : largest
  );
  
  return largestSection.type;
}
